import React, { useState, useEffect } from "react";
import { Grid, Typography } from '@mui/material';
import InstantApprovalChip from "../../../location/search/InstantApprovalChip";
import RequestSpaceChip from "../../../location/search/RequestSpaceChip";
import { PinDrop as PinDropIcon } from '@mui/icons-material';
import type {Supplier} from "../../../../types/Supplier";
import type moment from "moment";
import BookingDetailsDatePicker from "../BookingDetailsDatePicker";
import BookingDetailsEquipmentTypeSelect from "../BookingDetailsEquipmentTypeSelect";
import BookingDetailsNumberOfSpaces from "../BookingDetailsNumberOfSpaces";
import {bookingInfoStyles} from "./styles";
import useLocationAvailabilityCalendar from "../../../../hooks/components/location/useLocationAvailabilityCalendar";

interface BookingInfoProps {
  instantApproval: boolean;
  bookingLocation: Supplier;
  startDate: string;
  endDate: string;
  setStartDate: (value: string | moment.Moment) => void;
  setEndDate: (value: string | moment.Moment) => void;
  assetType: string;
  setAssetType?: (value: string) => void;
  monthly: boolean;
  numberSpaces: number;
  setNumberSpaces?: (value: number) => void;
  disableDatePickerPast: boolean;
  isBrokeredBooking: boolean;
  disableBookingChanges: boolean;
  assetTypelist: string[];
  onToggleEditMode?: (value: boolean) => void;
}

const BookingInfo = (props: BookingInfoProps) => {

  const {
    instantApproval,
    bookingLocation,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    assetType,
    setAssetType,
    monthly = false,
    numberSpaces,
    setNumberSpaces,
    disableDatePickerPast,
    isBrokeredBooking,
    disableBookingChanges,
    assetTypelist,
    onToggleEditMode,
  } = props;

  const [editBookingDates, setEditBookingDates] = useState(false);
  const [editEquipmentType, setEditEquipmentType] = useState(false);
  const [editNumberOfSpaces, setEditNumberOfSpaces] = useState(false);
  const {availability, loadMonth} = useLocationAvailabilityCalendar(bookingLocation?.locationId);

  useEffect(() => {
    if (!disableBookingChanges) onToggleEditMode?.(!editBookingDates && !editEquipmentType && !editNumberOfSpaces);
  }, [disableBookingChanges, onToggleEditMode, editBookingDates, editEquipmentType, editNumberOfSpaces]);

  return (
    <Grid item container direction={'column'} spacing={2}>
      <Grid item container>
        <Grid item container justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="body1" sx={bookingInfoStyles.locationName}>{bookingLocation?.locationName}</Typography>
          {instantApproval ? <Grid item><InstantApprovalChip/></Grid> : <Grid item><RequestSpaceChip/></Grid>}
        </Grid>
        <Grid item className="ss-byb-details-city-container">
          <PinDropIcon fontSize="small" sx={bookingInfoStyles.pinDropIcon}/>
          {/* TODO: add country name when applicable */}
          <Typography variant="subtitle1"
                      sx={bookingInfoStyles.cityName}>{`${bookingLocation?.city}, ${bookingLocation?.state}`}</Typography>
        </Grid>
      </Grid>
      <Grid item container>
        <Grid item container className="ss-byb-details-child" xs={12}>
          <BookingDetailsDatePicker
            editMode={editBookingDates}
            onToggleEditMode={setEditBookingDates}
            showEndDate={!monthly}
            fromDate={startDate}
            onFromDateChange={setStartDate}
            toDate={endDate}
            onToDateChange={setEndDate}
            readOnly={disableBookingChanges}
            disablePast={disableDatePickerPast}
            availability={availability}
            onCalendarMonthChange={loadMonth}
          />
        </Grid>
        <Grid item className={'ss-byb-details-child'} xs={12}>
          <BookingDetailsEquipmentTypeSelect
            editMode={editEquipmentType}
            onToggleEditMode={setEditEquipmentType}
            value={assetType}
            onChange={setAssetType}
            options={assetTypelist}
            selectClasses={{root: "w-100", label: ""}}
            disableBookingChanges={disableBookingChanges}
            readOnly={disableBookingChanges}
          />
        </Grid>
        <Grid container item className="ss-byb-details-child" xs={12}>
          <BookingDetailsNumberOfSpaces
            editMode={editNumberOfSpaces}
            onToggleEditMode={setEditNumberOfSpaces}
            bookingLocation={bookingLocation}
            onChange={setNumberSpaces ?? (() => {})}
            value={numberSpaces}
            readOnly={disableBookingChanges}
            overrideMinimum={isBrokeredBooking}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default BookingInfo;
